import { SxProps } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import {
  Filter,
  FilterDropdown,
  FilterList,
} from "@outschool/ui-components-website";
import { Checkbox } from "@outschool/ui-legacy-component-library";
import { without } from "lodash";
import React, { ReactNode } from "react";

import { usGradeLevels } from "../../../../shared/Activity";

type GradeProficiencyLevelFilterProps = {
  value: Array<string>;
  onChange: (newValue: Array<string>) => void;
  openFilterDropdown: string | null;
  onClose: () => void;
  onOpen: () => void;
  referenceElement: React.RefObject<any> | undefined;
  filterDropdownSx?: SxProps;
  applyButton?: ReactNode;
};

export function GradeLevelFilter({
  value,
  onChange,
  openFilterDropdown,
  onClose,
  onOpen,
  referenceElement,
  filterDropdownSx,
  applyButton,
}: GradeProficiencyLevelFilterProps) {
  const { t } = useTranslation(
    "client\\components\\search\\desktop\\LevelFilters",
  );
  const filterOptions = LevelFilter<string>({
    title: t("US grade level"),
    value,
    filterOptions: usGradeLevels,
    onChange,
  });

  return (
    <FilterDropdown
      filterName={t("Grade")}
      filterValue={
        value?.length > 1
          ? t("{{numGradeLevels}} grades", {
              numGradeLevels: value?.length,
            })
          : value?.[0]
      }
      open={openFilterDropdown === "gradeLevel"}
      onOpen={onOpen}
      onClose={onClose}
      referenceElement={referenceElement}
      buttonSx={filterDropdownSx}
    >
      {filterOptions}
      {applyButton}
    </FilterDropdown>
  );
}

type LevelFilterProps<T> = {
  title: string;
  filterOptions: Array<T>;
  value: Array<T>;
  onChange: (newValue: Array<T>) => void;
  handleChangesExternally?: boolean;
  listSx?: SxProps;
};

export function LevelFilter<T>({
  title,
  value,
  filterOptions,
  onChange,
  listSx,
}: LevelFilterProps<T>) {
  const { t } = useTranslation(
    "client\\components\\search\\desktop\\LevelFilters",
  );
  const [levels, setLevels] = React.useState(value || []);
  const handleFilterChange = (value: T[]) => {
    setLevels(value);
    onChange(value);
  };

  return (
    <FilterList sx={listSx}>
      <Filter title={title}>
        {filterOptions.map((level: T) => (
          <Checkbox
            key={`${level}`}
            id={`${level}`}
            label={t("{{level}}", { level })}
            checked={value?.includes(level)}
            onChange={e => {
              const checked = e.target.checked;
              handleFilterChange(
                checked ? [...levels, level] : without(levels, level),
              );
            }}
            sx={{ marginBottom: "small" }}
          />
        ))}
      </Filter>
    </FilterList>
  );
}
